/**
 * Features Manifest — Cache singleton de nível de módulo.
 *
 * Carregado UMA vez no boot da aplicação (PermissionContext.tsx).
 * Substitui os arrays hardcoded TOOLS_NO_PERMISSION_REQUIRED e TOOL_PERMISSION_MAP
 * com dados vindos do banco via GET /permissions/features.
 *
 * Funções exportadas:
 *   loadFeaturesManifest()         — chama a API e preenche o cache
 *   isToolFreeFromManifest(toolId) — null = cache não carregado ainda
 *   getFeatureKeyFromManifest(toolId) — null = cache não carregado
 *   getFeaturesManifest()          — retorna o manifest completo ou null
 */

import { api } from "../api";
import { TokenService } from "../tokenService";

export interface ManifestFeature {
	feature_key: string;
	category: string;
	label: string;
	description: string | null;
	default_value: boolean;
	requires_permission: boolean;
	tool_aliases: string[];
	sort_order: number;
}

export interface FeaturesManifest {
	/** IDs/aliases de ferramentas livres (requires_permission = false) */
	freeToolIds: Set<string>;
	/** toolId / alias → feature_key */
	aliasToFeatureKey: Map<string, string>;
	/** Ferramentas que requerem permissão (category='tools', requires_permission=true) */
	toolFeatures: ManifestFeature[];
	/** Ferramentas livres (category='tools', requires_permission=false) */
	freeTools: ManifestFeature[];
	/** Features gerais (category='general') */
	generalFeatures: ManifestFeature[];
	/** Features do calculator (category='calculator') */
	calculatorFeatures: ManifestFeature[];
	/** Features de camisetas (category='tshirts') */
	tshirtsFeatures: ManifestFeature[];
	/** Todas as features do catálogo */
	allFeatures: ManifestFeature[];
}

let manifest: FeaturesManifest | null = null;
let manifestPromise: Promise<void> | null = null;

/**
 * Carrega o catálogo de features da API e preenche o cache singleton.
 * Seguro chamar múltiplas vezes — a segunda chamada aguarda a primeira.
 */
export async function loadFeaturesManifest(): Promise<void> {
	if (manifest !== null) return;
	if (manifestPromise !== null) return manifestPromise;

	// Guard: não chamar API sem token (evita 403 na tela de login)
	if (!TokenService.getAccessToken()) return;

	manifestPromise = (async () => {
		try {
			const response = await api("/permissions/features", { method: "GET" });

			if (!response?.success || !Array.isArray(response?.data)) {
				// API indisponível ou formato inesperado — resetar promise para permitir retry
				manifestPromise = null;
				return;
			}

			const features: ManifestFeature[] = response.data.map((f: any) => ({
				feature_key: String(f.feature_key || ""),
				category: String(f.category || ""),
				label: String(f.label || f.feature_key || ""),
				description: f.description ? String(f.description) : null,
				default_value: f.default_value === true,
				requires_permission: f.requires_permission !== false, // default true se coluna não existe ainda
				tool_aliases: Array.isArray(f.tool_aliases)
					? f.tool_aliases.map(String)
					: [],
				sort_order: Number(f.sort_order) || 0,
			}));

			const freeToolIds = new Set<string>();
			const aliasToFeatureKey = new Map<string, string>();

			for (const f of features) {
				// Sufixo implícito: 'tool.onecalc' → 'onecalc'
				const implicitId = f.feature_key.includes(".")
					? f.feature_key.split(".").slice(1).join(".")
					: f.feature_key;

				// Construir conjunto de aliases: tool_aliases + sufixo implícito (para tools)
				const allAliases: string[] =
					f.tool_aliases.length > 0
						? f.tool_aliases
						: f.category === "tools"
							? [implicitId]
							: [];

				for (const alias of allAliases) {
					aliasToFeatureKey.set(alias, f.feature_key);
					if (!f.requires_permission) {
						freeToolIds.add(alias);
					}
				}

				// Sempre mapear o sufixo implícito e a feature_key completa
				aliasToFeatureKey.set(implicitId, f.feature_key);
				aliasToFeatureKey.set(f.feature_key, f.feature_key);

				if (!f.requires_permission) {
					freeToolIds.add(implicitId);
					freeToolIds.add(f.feature_key);
				}
			}

			manifest = {
				freeToolIds,
				aliasToFeatureKey,
				toolFeatures: features.filter(
					(f) => f.category === "tools" && f.requires_permission,
				),
				freeTools: features.filter(
					(f) => f.category === "tools" && !f.requires_permission,
				),
				generalFeatures: features.filter((f) => f.category === "general"),
				calculatorFeatures: features.filter((f) => f.category === "calculator"),
				tshirtsFeatures: features.filter((f) => f.category === "tshirts"),
				allFeatures: features,
			};
		} catch {
			// Falha silenciosa — o código de permissão fará fallback para as constantes hardcoded
			manifestPromise = null;
		}
	})();

	return manifestPromise;
}

/**
 * Verifica se uma ferramenta é livre (não requer verificação de permissão).
 * @returns true se livre, false se requer permissão, null se manifest não carregado OU tool desconhecida.
 * Retornar null para tools desconhecidas permite fallback para a lista hardcoded.
 */
export function isToolFreeFromManifest(toolId: string): boolean | null {
	if (manifest === null) return null;
	// Se a tool é conhecida pelo manifest (existe como alias ou feature_key), responder com certeza
	if (manifest.freeToolIds.has(toolId)) return true;
	if (manifest.aliasToFeatureKey.has(toolId)) return false; // conhecida, requer permissão
	// Tool desconhecida pelo banco — retornar null para permitir fallback hardcoded
	return null;
}

/**
 * Resolve um toolId (ou alias) para sua feature_key no banco.
 * @returns feature_key se encontrada, null se manifest não carregado ou alias desconhecido.
 */
export function getFeatureKeyFromManifest(toolId: string): string | null {
	if (manifest === null) return null;
	return manifest.aliasToFeatureKey.get(toolId) ?? null;
}

/**
 * Retorna o manifest completo ou null se ainda não carregado.
 */
export function getFeaturesManifest(): FeaturesManifest | null {
	return manifest;
}

/**
 * Reseta o manifest (útil para testes ou logout).
 */
export function resetFeaturesManifest(): void {
	manifest = null;
	manifestPromise = null;
}
